import { useModel } from "@stem-sims/nexus"
import { Button, Col, Form, Row } from "react-bootstrap"
import { PulseLoader } from "react-spinners"
import React, { useCallback, useState } from "react"
import EditingItemPane, { FieldType } from "../EditingItemPane"
import { createReview, deleteReview, getReviews, updateReview } from "shared/routes/reviewRoutes"
import { toast } from "react-toastify"
import EditingListPane from "../EditingListPane"
import assessments from "shared/routes/assessments/assessments"
import lessons from "shared/lessons"
import SelectModal from "../Components/SelectModal"
import SelectReviewGroupsModal from "../Components/SelectReviewGroupsModal"

export default function EditingReview() {

    const [groupedReviewsHidden, toggleHideGroupedReviews] = useState<boolean>(false)
    const { loading: reviewLoading, response: reviews, refreshModel: reviewRefresh } = useModel({
        model: getReviews,
        props: { excludeGroupedReviews: groupedReviewsHidden },
    })

    const [reviewId, setReviewId] = useState<string>(null)
    const selectedIndex = reviews?.findIndex((review) => review.id === reviewId)
    const review = reviews?.[selectedIndex]

    const [selectModal, setSelectModal] = useState<string>(null)

    const { response: assessment } = useModel({
        model: assessments.findById,
        props: { id: review?.pretestId },
    })

    const { response: lesson } = useModel({
        model: lessons.findById,
        props: review?.lessonId ?? undefined,
    })

    const { response: postAssessment } = useModel({
        model: assessments.findById,
        props: { id: review?.postTestId },
    })

    const { response: grade3assessment } = useModel({
        model: assessments.findById,
        props: { id: review?.grade3pretestId },
    })

    const { response: grade3lesson } = useModel({
        model: lessons.findById,
        props: review?.grade3lessonId ?? undefined,
    })

    const { response: grade3postAssessment } = useModel({
        model: assessments.findById,
        props: { id: review?.grade3postTestId },
    })


    const onSubmitCallback = React.useCallback(async (values) => {
        try {
            const message = await updateReview({
                reviewId,
                title: values.title,
                pretestId: values.pretestId ?? undefined,
                postTestId: values.postTestId ?? undefined,
                lessonId: values.lessonId ?? undefined,
                grade3lessonId: values.grade3lessonId ?? undefined,
                image: values.image ?? undefined,
            })
            toast.success(message)
            reviewRefresh()
        } catch (err) {
            toast.error(err)
        }
    }, [reviewId, reviewRefresh])

    const createReviewCallback = useCallback(async () => {
        try {
            const message = await createReview()
            toast.success(message)
            reviewRefresh()
        } catch (err) {
            toast.error(err)
        }
    }, [reviewRefresh])

    const updateReviewCallback = useCallback(async (values: { id: string }) => {
        setReviewId(reviewId === values.id ? null : values.id)
    }, [reviewId])

    const deleteReviewCallback = useCallback(async (values: { id: string }) => {
        try {
            const message = await deleteReview(values.id)
            toast.success(message)
            reviewRefresh()
        } catch (err) {
            toast.error(err)
        }
    }, [reviewRefresh])

    if (!review && reviewLoading) {
        return <>
            <PulseLoader />
        </>
    }

    const fields: FieldType[] = [
        { fieldName: "title", inputType: "input" },
        {
            fieldName: "pretestId", inputType: "special", render: () => (<>
                {assessment && <> 
                    <span className="ms-2 text-primary fw-bold">{assessment.id}</span>
                    <p className="my-0">Selected: {assessment.module} - {assessment.type} - #{assessment.number} ({assessment.title})</p>
                </>}
                {!assessment && <span className="text-danger"> Not Selected</span>}
                <Button variant="theme" className="d-block" onClick={() => { setSelectModal("assessment") }}>
                    Select Assessment
                </Button>
            </>)
        },
        { 
            fieldName: "lessonId", inputType: "special", render: () => (<>
                {lesson && <> 
                    <span className="ms-2 text-primary fw-bold">{lesson.id}</span>
                    <p className="my-0">Selected: {lesson.moduleName} - {lesson.type} - #{lesson.number} ({lesson.title})</p>
                </>}
                {!lesson && <span className="text-danger"> Not Selected</span>}
                <Button variant="theme" className="d-block" onClick={() => { setSelectModal("lesson") }}>
                    Select Lesson
                </Button>
            </>)
        },
        {
            fieldName: "postTestId", inputType: "special", render: () => (<>
                {postAssessment && <> 
                    <span className="ms-2 text-primary fw-bold">{postAssessment.id}</span>
                    <p className="my-0">Selected: {postAssessment.module} - {postAssessment.type} - #{postAssessment.number} ({postAssessment.title})</p>
                </>}
                {!postAssessment && <span className="text-warning"> Not Selected</span>}
                <Button variant="theme" className="d-block" onClick={() => { setSelectModal("postAssessment") }}>
                    Select postAssessment
                </Button>
            </>)
        },
        { 
            fieldName: "grade 3 pretestId", inputType: "special", render: () => (<>
                {grade3assessment && <> 
                    <span className="ms-2 text-primary fw-bold">{grade3assessment.id}</span>
                    <p className="my-0">Selected: {grade3assessment.module} - {grade3assessment.type} - #{grade3assessment.number} ({grade3assessment.title})</p>
                </>}
                {!grade3assessment && <span className="text-warning"> Not Selected</span>}
                <span className="mx-0 row justify-content-between">
                    <Button variant="theme" className="col-auto" onClick={() => { setSelectModal("grade3assessment") }}>
                        Select Grade 3 Assessment
                    </Button>
                    {grade3assessment && <Button variant="secondary" className="col-auto" onClick={async () => {
                        await updateReview({
                            reviewId: reviewId,
                            grade3pretestId: "deselect"
                        })
                        reviewRefresh()
                        toast.success("Deselected successfully.")
                    }}>
                        Deselect Grade 3 Assessment
                    </Button>}
                </span>
            </>)
        },
        { 
            fieldName: "grade 3 lessonId", inputType: "special", render: () => (<>
                {grade3lesson && <> 
                    <span className="ms-2 text-primary fw-bold">{grade3lesson.id}</span>
                    <p className="my-0">Selected: {grade3lesson.moduleName} - {grade3lesson.type} - #{grade3lesson.number} ({grade3lesson.title})</p>
                </>}
                {!grade3lesson && <span className="text-warning"> Not Selected</span>}
                <span className="mx-0 row justify-content-between">
                    <Button variant="theme" className="col-auto" onClick={() => { setSelectModal("grade 3 lesson") }}>
                        Select Grade 3 Lesson
                    </Button>
                    {grade3lesson && <Button variant="secondary" className="col-auto" onClick={async () => {
                        await updateReview({
                            reviewId: reviewId,
                            grade3lessonId: "deselect"
                        })
                        reviewRefresh()
                        toast.success("Deselected successfully.")
                    }}>
                        Deselect Grade 3 Lesson
                    </Button>}
                </span>
            </>)
        },
        { 
            fieldName: "grade 3 postTestId", inputType: "special", render: () => (<>
                {grade3postAssessment && <> 
                    <span className="ms-2 text-primary fw-bold">{grade3postAssessment.id}</span>
                    <p className="my-0">Selected: {grade3postAssessment.module} - {grade3postAssessment.type} - #{grade3postAssessment.number} ({grade3postAssessment.title})</p>
                </>}
                {!grade3postAssessment && <span className="text-warning"> Not Selected</span>}
                <span className="mx-0 row justify-content-between">
                    <Button variant="theme" className="col-auto" onClick={() => { setSelectModal("grade3assessment") }}>
                        Select Grade 3 postAssessment
                    </Button>
                    {grade3postAssessment && <Button variant="secondary" className="col-auto" onClick={async () => {
                        await updateReview({
                            reviewId: reviewId,
                            grade3postTestId: "deselect"
                        })
                        reviewRefresh()
                        toast.success("Deselected successfully.")
                    }}>
                        Deselect Grade 3 postAssessment
                    </Button>}
                </span>
            </>)
        },
        { 
            fieldName: "review groups", inputType: "special", render: () => (<>
                {review.groups && <p>{review.groups.length} groups selected</p>}
                <Button variant="theme" className="d-block" onClick={() => { setSelectModal("review-groups") }}>
                    Select Review Groups
                </Button>
            </>)
        },
        { fieldName: "image", inputType: "image" },
    ]

    return <div>
        <Form className="d-flex justify-content-center text-primary fw-bold mx-4">
          <Form.Check
              onChange={(e) => toggleHideGroupedReviews(e.currentTarget.checked)}
              checked={groupedReviewsHidden}
              type="switch"
              id="hide-grouped-reviews-switch"
              label={<span>Hide Grouped Reviews</span>}
          />
        </Form>
        <Row>
            <Col>
                <h3>Reviews</h3>
                <EditingListPane
                    parent={null}
                    items={reviews?.map((review) => ({ id: review.id, name: review.title })) ?? []}
                    selectedIndex={selectedIndex}
                    itemType="review"
                    createItem={createReviewCallback}
                    updateItem={updateReviewCallback}
                    deleteItem={deleteReviewCallback}
                />
                <SelectModal 
                    selectModal={["assessment", "postAssessment", "lesson", "grade3assessment", "grade3postAssessment","grade 3 lesson"].includes(selectModal) ? selectModal : null} 
                    onHide={() => setSelectModal(null)}
                    includeUnlinkedAssessments={["assessment", "postAssessment", "grade3assessment", "grade3postAssessment"].includes(selectModal) ? true : undefined}
                    onSelection={(selectedLesson, _selectedStudent, selectedAssessment) => {
                        return (
                            <Button
                                onClick={async () => {
                                    const selectedAssessmentId = selectModal === "assessment" ? (selectedAssessment?.id ?? selectedLesson?.assessment?.id) : undefined
                                    const selectedLessonId = selectModal === "lesson" ? selectedLesson?.id : undefined
                                    const selectedPostAssessmentId = selectModal === "postAssessment" ? (selectedAssessment?.id ?? selectedLesson?.assessment?.id) : undefined
                                    const selectedGrade3AssessmentId = selectModal === "grade3assessment" ? (selectedAssessment?.id ?? selectedLesson?.assessment?.id) : undefined
                                    const selectedGrade3LessonId = selectModal === "grade 3 lesson" ? selectedLesson?.id : undefined
                                    const selectedGrade3PostAssessmentId = selectModal === "grade3postAssessment" ? (selectedAssessment?.id ?? selectedLesson?.assessment?.id) : undefined

                                    await updateReview({
                                        reviewId: reviewId,
                                        pretestId: selectedAssessmentId,
                                        lessonId: selectedLessonId,
                                        postTestId: selectedPostAssessmentId,
                                        grade3pretestId: selectedGrade3AssessmentId,
                                        grade3lessonId: selectedGrade3LessonId,
                                        grade3postTestId: selectedGrade3PostAssessmentId,
                                    })
                                    reviewRefresh()
                                    setSelectModal(null)
                                    toast.success("Updated successfully.")
                                }}
                            >Select {selectModal}</Button>
                        )
                    }} />

                    <SelectReviewGroupsModal 
                        show={selectModal === "review-groups"} 
                        onHide={() => setSelectModal(null)} 
                        groupIds={review?.groups?.map(g => g.groupId)}
                        onSelection={async (groupIds: string[]) => {
                            await updateReview({
                                reviewId: reviewId,
                                groupIds: groupIds,
                            })
                            setSelectModal(null)
                            toast.success("Updated groups successfully.")
                            reviewRefresh()
                    }} />
            </Col>

            <Col>
            {review ? <>
            <h3>{review.title}</h3>
                <EditingItemPane
                    data={{
                        ...review,
                    }}
                    fields={fields}
                    itemTypeName="review"
                    onSubmit={onSubmitCallback}
                />
            </> : <>
                <h3>Select a review</h3>
            </>}

            </Col>
        </Row>
    </div>
}
